import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CreatedDocument {
  id: string;
  name: string;
  createdAt: string;
  modifiedTime: string;
}

interface GoogleDocsState {
  accessToken: string | null;
  currentDocId: string | null;
  tokenExpiry: number | null; // timestamp when token expires
  createdDocuments: CreatedDocument[]; // All documents created by our app
}

const initialState: GoogleDocsState = {
  accessToken: null,
  currentDocId: null,
  tokenExpiry: null,
  createdDocuments: [],
};

const googleDocsSlice = createSlice({
  name: 'googleDocs',
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<{ token: string; expiresIn?: number }>) => {
      state.accessToken = action.payload.token;
      // Set expiry timestamp (default to 1 hour if not provided)
      const expirySeconds = action.payload.expiresIn || 3600;
      state.tokenExpiry = Date.now() + (expirySeconds * 1000);
    },
    setCurrentDocId: (state, action: PayloadAction<string | null>) => {
      state.currentDocId = action.payload;
    },
    addCreatedDocument: (state, action: PayloadAction<CreatedDocument>) => {
      // Check if document already exists in the list
      const existingIndex = state.createdDocuments.findIndex(
        doc => doc.id === action.payload.id
      );

      if (existingIndex >= 0) {
        // Update existing document
        state.createdDocuments[existingIndex] = action.payload;
      } else {
        // Add new document at the beginning
        state.createdDocuments.unshift(action.payload);
      }
    },
    removeCreatedDocument: (state, action: PayloadAction<string>) => {
      state.createdDocuments = state.createdDocuments.filter(
        doc => doc.id !== action.payload
      );
    },
    clearGoogleDocsAuth: (state) => {
      state.accessToken = null;
      state.currentDocId = null;
      state.tokenExpiry = null;
      // Keep created documents even after logout
    },
  },
});

export const {
  setAccessToken,
  setCurrentDocId,
  addCreatedDocument,
  removeCreatedDocument,
  clearGoogleDocsAuth
} = googleDocsSlice.actions;

export default googleDocsSlice.reducer;
